import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, X, Minus, ChevronUp, Loader2, BadgeCheck } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAuth } from "@/context/AuthContext";
import { useRealtime, useRealtimeEvent } from "@/context/RealtimeContext";
import { useChatDock } from "@/context/ChatDockContext";
import { fullTime, messageStamp } from "@/lib/timeago";
import { ChatMessageText } from "@/components/social/ChatMessageText";
import { MessageAvatar } from "@/components/social/MessageAvatar";
import { EmojiPickerButton } from "@/components/social/EmojiPickerButton";
import { ReactionBar } from "@/components/social/ReactionBar";
import { AuthorLink } from "@/components/social/AuthorLink";
import { applyReactionToggle } from "@/lib/reactions";
import { cn } from "@/lib/utils";

function ChatWindow({ chat, minimized, onClose, onToggle }) {
  const { user } = useAuth();
  const { reloadMessageUnread } = useRealtime();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [unread, setUnread] = useState(0);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const [readOnly, setReadOnly] = useState(null);
  const seenRef = useRef(new Set());

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/messages/conversations/${chat.id}/messages`);
      setReadOnly(data.can_send === false ? (data.why_text || "You can't reply to this conversation.") : null);
      // Merge by id so an optimistic/just-sent message is never clobbered by an
      // in-flight fetch that was issued before it existed.
      setMessages((prev) => {
        const map = new Map();
        [...(data.messages || []), ...prev].forEach((m) => map.set(m.id, m));
        return [...map.values()].sort((a, b) => (a.created_at < b.created_at ? -1 : 1));
      });
      if (reloadMessageUnread) reloadMessageUnread();
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => { if (!minimized) load(); /* eslint-disable-next-line */ }, [chat.id, minimized]);
  // Clear the unread badge whenever the window is opened/restored.
  useEffect(() => { if (!minimized) setUnread(0); }, [minimized]);

  useRealtimeEvent((evt) => {
    if (evt.type !== "message" || evt.data.conversation_id !== chat.id) return;
    const msg = evt.data.message;
    if (seenRef.current.has(msg.id)) return; // ignore duplicate delivery (reconnects / StrictMode double-WS)
    seenRef.current.add(msg.id);
    setMessages((prev) => (prev.find((m) => m.id === msg.id) ? prev : [...prev, msg]));
    if (!minimized) api.post(`/messages/conversations/${chat.id}/read`).catch(() => {});
    else if (msg.sender_id !== (user && user.user_id)) setUnread((u) => u + 1);
    if (reloadMessageUnread) reloadMessageUnread();
  });

  useRealtimeEvent((evt) => {
    if (evt.type === "message_reaction" && evt.data.conversation_id === chat.id) {
      setMessages((prev) => prev.map((m) => (m.id === evt.data.message_id ? { ...m, reactions: evt.data.reactions } : m)));
    }
    // Platform User Blocking V1 — re-read the thread so an open window picks up its
    // read-only state without a reload or a fresh sign-in.
    if (evt.type === "relationship_state_changed" && !minimized) load();
  });

  const react = async (messageId, key) => {
    setMessages((prev) => prev.map((m) => (m.id === messageId ? applyReactionToggle(m, key) : m)));
    try { await api.post(`/messages/conversations/${chat.id}/messages/${messageId}/react`, { emoji: key }); }
    catch (e) { /* ignore; realtime/refetch will reconcile */ }
  };

  const send = async () => {
    if (!text.trim()) return;
    const body = text.trim();
    setText(""); setSending(true);
    try {
      const { data } = await api.post(`/messages/conversations/${chat.id}`, { body });
      setMessages((prev) => [...prev, data]);
    } catch (e) { toast.error("Could not send"); setText(body); }
    finally { setSending(false); }
  };
  useEffect(() => { if (!minimized && bottomRef.current) bottomRef.current.scrollIntoView(); }, [messages, minimized]);

  const other = chat.other || {};
  const header = (
    <div className="flex items-center gap-2 bg-primary px-3 py-2 text-primary-foreground">
      <button onClick={onToggle} aria-label={minimized ? "Expand chat" : "Minimize chat"} className="shrink-0" data-testid={`chatdock-header-${chat.id}`}>
        <Avatar className="h-7 w-7">
          {other.headshot_url && <AvatarImage src={fileUrl(other.headshot_url)} alt="" />}
          <AvatarFallback className="bg-teal-700 text-[11px] text-white">{(other.display_name || "?")[0]}</AvatarFallback>
        </Avatar>
      </button>
      <div className="min-w-0 flex-1 truncate">
        <AuthorLink card={other} className="text-sm font-[620] text-primary-foreground hover:underline" testId={`chatdock-name-${chat.id}`} fallback="Member" />
        {other && other.official && (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-white/20 px-1.5 py-0.5 text-[10px] font-[620] text-primary-foreground" data-testid={`chatdock-official-${chat.id}`}>
            <BadgeCheck className="h-3 w-3" /> Official
          </span>
        )}
      </div>
      {minimized && unread > 0 && (
        <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-white px-1.5 text-[11px] font-[680] text-primary" data-testid={`chatdock-unread-${chat.id}`}>{unread > 99 ? "99+" : unread}</span>
      )}
      <button onClick={onToggle} aria-label={minimized ? "Expand" : "Minimize"} className="opacity-90 transition-opacity hover:opacity-100">{minimized ? <ChevronUp className="h-4 w-4" /> : <Minus className="h-4 w-4" />}</button>
      <button onClick={onClose} aria-label="Close chat" data-testid={`chatdock-close-${chat.id}`} className="opacity-90 transition-opacity hover:opacity-100"><X className="h-4 w-4" /></button>
    </div>
  );

  if (minimized) {
    return <div className="w-full overflow-hidden border border-border bg-card shadow-lg md:w-60">{header}</div>;
  }
  return (
    <div className="flex h-[420px] max-h-[75vh] w-full max-w-none flex-col overflow-hidden border border-border bg-card shadow-2xl md:w-80 md:max-w-[88vw]" data-testid={`chatdock-window-${chat.id}`}>
      {header}
      <ScrollArea className="flex-1 px-3 py-3">
        {loading ? (
          <div className="flex h-full items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="space-y-2">
            {messages.map((m) => {
              const mine = m.sender_id === (user && user.user_id);
              return (
                <div key={m.id} className={cn("group flex items-end gap-2", mine ? "flex-row-reverse" : "flex-row")} data-testid={`chatdock-msg-${m.id}`}>
                  {/* Whose message this is, at a glance — and a tap away from their
                      profile. Same component as the Messages page and Group Member
                      Messages so all three agree. */}
                  <MessageAvatar card={other} mine={mine} size="h-7 w-7"
                                 testId={`chatdock-msg-avatar-${m.id}`} />
                  <div className={cn("flex min-w-0 flex-col", mine ? "items-end" : "items-start")}>
                  <div className={cn("max-w-[80%] rounded-2xl px-3 py-1.5 text-sm leading-5", mine ? "rounded-br-sm bg-primary text-primary-foreground" : "rounded-bl-sm bg-secondary text-foreground")} title={fullTime(m.created_at)}>
                    <ChatMessageText body={m.body} />
                  </div>
                  <div className={cn("mt-0.5", (!m.reactions || Object.keys(m.reactions).length === 0) && "opacity-100 transition-opacity md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100")}>
                    <ReactionBar reactions={m.reactions} myReaction={m.my_reaction} onReact={(k) => react(m.id, k)} popoverClassName="z-[95]" testId={`chatdock-react-${m.id}`} />
                  </div>
                  <span className="px-1 text-[10px] text-muted-foreground">{messageStamp(m.created_at, user && user.timezone)}</span>
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>
        )}
      </ScrollArea>
      {readOnly ? (
        <div className="border-t border-border p-3 text-center text-xs text-muted-foreground" data-testid={`chatdock-readonly-${chat.id}`}>
          {readOnly}
        </div>
      ) : (
      <div className="flex items-center gap-1 border-t border-border p-2">
        <EmojiPickerButton targetRef={inputRef} value={text} onChange={setText} testId={`chatdock-emoji-${chat.id}`} />
        <Input ref={inputRef} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()} placeholder="Message…" className="h-9" data-testid={`chatdock-input-${chat.id}`} />
        <Button size="icon" className="h-9 w-9 shrink-0" onClick={send} disabled={sending || !text.trim()} data-testid={`chatdock-send-${chat.id}`}>
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
      )}
    </div>
  );
}

export function ChatDock() {
  const { user } = useAuth();
  const { chats, minimized, closeChat, toggleMinimize } = useChatDock();
  const docked = Boolean(user) && chats.length > 0;
  // The Charla bubble sits over the dock's Send button on phones — park it while chatting.
  useEffect(() => {
    if (!docked) return undefined;
    document.body.classList.add("hide-charla-mobile");
    return () => document.body.classList.remove("hide-charla-mobile");
  }, [docked]);
  if (!docked) return null;
  return (
    <div className="fixed bottom-2 left-2 right-2 z-[70] flex items-end justify-end gap-3 md:left-auto md:right-24" data-testid="chat-dock">
      {chats.map((c, i) => (
        <div key={c.id} className={cn("min-w-0", i < chats.length - 1 ? "hidden md:block" : "block w-full md:w-auto")}>
          <ChatWindow chat={c} minimized={minimized.includes(c.id)} onClose={() => closeChat(c.id)} onToggle={() => toggleMinimize(c.id)} />
        </div>
      ))}
    </div>
  );
}
